import { Button, Input } from "@wmart/ui";
import type { VolumeBreakDTO } from "@wmart/shared-types";
import {
  validateBreaksAboveMoq,
  detectDuplicateMinQuantities,
} from "../lib/volume-breaks-validation.js";

interface Props {
  value: VolumeBreakDTO[];
  variantMoq: number;
  onChange: (next: VolumeBreakDTO[]) => void;
}

export function VolumeBreaksEditor({ value, variantMoq, onChange }: Props) {
  const belowMoq = new Set(validateBreaksAboveMoq(value, variantMoq));
  const dupes = new Set(detectDuplicateMinQuantities(value));

  return (
    <div className="flex flex-col gap-3">
      <table className="w-full text-body-sm">
        <thead>
          <tr className="border-b border-ink-100 text-left text-ink-500">
            <th className="pb-1.5 pr-3 font-medium">Qty ≥</th>
            <th className="pb-1.5 pr-3 font-medium">Unit price (minor)</th>
            <th className="pb-1.5 w-10" />
          </tr>
        </thead>
        <tbody>
          {value.map((row, i) => (
            <tr key={row.id !== "" ? row.id : `new-${i}`} className="align-top">
              <td className="py-1.5 pr-3">
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={String(row.min_quantity)}
                  error={
                    belowMoq.has(i)
                      ? `Below MOQ (${variantMoq})`
                      : dupes.has(i)
                        ? "Duplicate min_quantity"
                        : undefined
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw === "" ? 1 : Number(raw);
                    onChange(
                      value.map((r, j) =>
                        j === i ? { ...r, min_quantity: next } : r,
                      ),
                    );
                  }}
                />
              </td>
              <td className="py-1.5 pr-3">
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={String(row.unit_price_minor)}
                  onChange={(e) =>
                    onChange(
                      value.map((r, j) =>
                        j === i
                          ? { ...r, unit_price_minor: Number(e.target.value) }
                          : r,
                      ),
                    )
                  }
                />
              </td>
              <td className="py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove tier ${i + 1}`}
                  onClick={() => onChange(value.filter((_, j) => j !== i))}
                >
                  ×
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            onChange([
              ...value,
              {
                id: "",
                variant_id: "",
                min_quantity: variantMoq,
                unit_price_minor: 0,
                currency: "GBP",
                deleted_at: null,
              },
            ])
          }
        >
          + Add tier
        </Button>
      </div>
    </div>
  );
}
